import { difference, flatten, groupBy, isEmpty, isNil, uniq } from "lodash";

import {
  CategoryMap,
  Product,
  ProductId,
  ProductKey,
  ProductSpec,
  ProductSpecKey,
  ProductType,
  ProductVariant,
  SpecDefinition,
  SpecDefinitions,
  SpecDefinitionUseCase,
  SpecGroup,
  SpecMap,
  StandaloneProductVariant
} from "../specs/product";

import { assert } from "./assert";
import shallowOmit from "./shallow-omit";

export type SpecItem<Spec extends ProductSpec> = {
  [K in keyof Spec]: {
    key: K;
    values: Array<NonNullable<Spec[K]>>;
  };
}[keyof Spec];

export interface GroupedSpecItems<Spec extends ProductSpec> {
  key: string;
  specs: SpecItem<Spec>[];
}

export interface VariantFilterValues<Type extends ProductType, K extends keyof SpecMap[Type]> {
  variant: StandaloneProductVariant<Type>;
  value: StandaloneProductVariant<Type>[K];
}

const isValueNotNilAndNotEmpty = <V>(value: V): value is NonNullable<V> => !isNil(value) && value !== "";

export const isStandaloneProductVariant = <Type extends ProductType>(
  product: Product<Type> | StandaloneProductVariant<Type>
): product is StandaloneProductVariant<Type> => !(ProductKey.Variants in product);

export const isProduct = <Type extends ProductType>(
  product: Product<Type> | StandaloneProductVariant<Type>
): product is Product<Type> => ProductKey.Variants in product;

/**
 * Returns all unique, non-empty and non-nil values for a given spec key of all variants in a product.
 */
export const getAllSpecValues = <Type extends ProductType, K extends keyof SpecMap[Type]>(
  specKey: K,
  product: Product<Type>
): Array<NonNullable<SpecMap[Type][K]>> => {
  const values: Array<Product<Type>[K] | ProductVariant<Type>[K]> =
    specKey in product ? [product[specKey]] : product[ProductKey.Variants].map(value => value[specKey]);

  return uniq(values as Partial<SpecMap[Type]>[K][]).filter(isValueNotNilAndNotEmpty);
};

/**
 * Returns the first value for a given spec key in a product.
 * Use this instead of getAllSpecValues if you are only interested in a single value of a spec key
 * e.g. like rendering the modelName of a product.
 */
export const getSingleSpecValue = <Type extends ProductType, K extends keyof SpecMap[Type]>(
  specKey: K,
  product: Product<Type> | StandaloneProductVariant<Type>
): SpecMap[Type][K] => {
  const variant = isProduct(product) ? getStandaloneProductVariant(product, product.variants[0]) : product;
  return variant[specKey];
};

export const isSpecValueVisible = <Type extends ProductType, K extends keyof SpecMap[Type]>(
  value: SpecMap[Type][K],
  specDefinition?: SpecDefinition<Type, K>
): value is NonNullable<SpecMap[Type][K]> => {
  assert(specDefinition, new Error("Spec value cannot be visible without spec definition."));
  return (
    isValueNotNilAndNotEmpty(value) && (specDefinition.visibleValue?.(value as NonNullable<SpecMap[Type][K]>) ?? true)
  );
};

export const filterVisibleSpecValues = <Type extends ProductType, K extends keyof SpecMap[Type]>(
  values: SpecMap[Type][K][],
  specDefinition?: SpecDefinition<Type, K>
): NonNullable<SpecMap[Type][K]>[] => {
  assert(specDefinition, new Error("Spec values cannot be filtered without spec definition."));

  if (specDefinition.visibleValues) {
    return specDefinition.visibleValues(values.filter(isValueNotNilAndNotEmpty));
  } else {
    return values.filter((value): value is NonNullable<SpecMap[Type][K]> => isSpecValueVisible(value, specDefinition));
  }
};

// Reads `ProductKey` inside the function, because a circular import leaves it undefined while this module loads
const getProductSpec = <Type extends ProductType>(product: Product<Type>): Omit<Product<Type>, ProductKey> =>
  shallowOmit(Object.values(ProductKey), product);

/*
 * ATTENTION:
 * The variant is created with object spread on purpose. JavaScript engines store an object compactly when all
 * its keys are added in one go. `Object.assign` adds the keys one by one, and for objects with as many keys as
 * a product (over 100) the engine then switches to a slower storage that needs about five times as much memory.
 * The content service keeps hundreds of thousands of variants in memory, so this saves gigabytes for big brands.
 */
const toStandaloneProductVariant = <Type extends ProductType>(
  productSpec: ReturnType<typeof getProductSpec<Type>>,
  product: Product<Type>,
  variant?: ProductVariant<Type>
): StandaloneProductVariant<Type> =>
  (variant
    ? {
        ...productSpec,
        ...variant,
        [ProductKey.ProductId]: product[ProductKey.ProductId],
        [ProductKey.ProductType]: product[ProductKey.ProductType],
        [ProductKey.VariantId]: variant[ProductKey.VariantId]
      }
    : {
        ...productSpec,
        [ProductKey.ProductId]: product[ProductKey.ProductId],
        [ProductKey.ProductType]: product[ProductKey.ProductType],
        [ProductKey.VariantId]: product[ProductKey.VariantId] ?? product[ProductKey.ProductId]
      }) as StandaloneProductVariant<Type>;

export const getStandaloneProductVariant = <Type extends ProductType>(
  product: Product<Type>,
  variant?: ProductVariant<Type>
): StandaloneProductVariant<Type> => toStandaloneProductVariant(getProductSpec(product), product, variant);

export const getStandaloneProductVariantById = <Type extends ProductType>(
  product: Product<Type>,
  variantId?: ProductId
): StandaloneProductVariant<Type> | undefined => {
  if (!variantId) {
    return undefined;
  }

  const variant = getProductVariants(product).find(variant => variant[ProductKey.VariantId] === variantId);

  return variant ? getStandaloneProductVariant(product, variant) : undefined;
};

export const getStandaloneProductVariants = <Type extends ProductType>(
  products: Product<Type> | Product<Type>[]
): StandaloneProductVariant<Type>[] => {
  const productsArr = Array.isArray(products) ? products : [products];
  return productsArr.flatMap(product => {
    // The product-level values are the same for all variants, so they are copied only once per product
    const productSpec = getProductSpec(product);
    return product.variants.length > 0
      ? product.variants.map(variant => toStandaloneProductVariant(productSpec, product, variant))
      : [toStandaloneProductVariant(productSpec, product)];
  });
};

/*
 * ATTENTION:
 * Grouping compares up to 180,000 variants with over 100 keys each, so the helpers below are written for speed.
 *
 * Reading a value by a key name that changes all the time (`item[key]`) is slow for JavaScript engines. All
 * variants of a product are created the same way, so their keys come in the same order. For these items the
 * values are read once with `Object.values` and then compared and copied by their position in that array, which
 * is much faster. Items whose keys differ from the first item are read by key name as before.
 */

// The values of each item in the key order of the first item, or undefined if the item has other keys
const getValueRows = <T>(list: T[], keys: (keyof T)[]): (unknown[] | undefined)[] =>
  list.map(item => {
    const itemKeys = Object.keys(item as object);
    return itemKeys.length === keys.length && itemKeys.every((key, index) => key === keys[index])
      ? Object.values(item as object)
      : undefined;
  });

// The positions of the keys whose values are the same in all items
const getCommonKeyIndexes = <T>(list: T[], keys: (keyof T)[], rows: (unknown[] | undefined)[]): number[] => {
  // The first item always has its own keys, so it always has a row
  const firstValues = rows[0] as unknown[];

  // Starts with all keys of the first item and drops every key whose value differs in another item
  return list.reduce(
    (indexes, item, itemIndex) => {
      const values = rows[itemIndex];
      // Chosen once per item, so comparing each value needs no extra check
      const hasSameValue = values
        ? (index: number) => values[index] === firstValues[index]
        : (index: number) => item[keys[index]] === firstValues[index];
      return indexes.every(hasSameValue) ? indexes : indexes.filter(hasSameValue);
    },
    keys.map((_, index) => index)
  );
};

export const groupStandaloneProductVariants = <Type extends ProductType>(
  variants: StandaloneProductVariant<Type>[]
): Product<Type>[] => {
  const groupedVariants = groupBy(variants, variant => `${variant[ProductKey.ProductId]}`);

  return Object.values(groupedVariants).map(group => {
    const keys = Object.keys(group[0]) as (keyof StandaloneProductVariant<Type> & string)[];
    const rows = getValueRows(group, keys);
    const firstValues = rows[0] as unknown[];
    const commonKeyIndexes = getCommonKeyIndexes(group, keys, rows);

    const commonValues: any = Object.fromEntries(commonKeyIndexes.map(index => [keys[index], firstValues[index]]));
    // One Set per product instead of one per variant, because it is used for every variant
    const keysToOmit = new Set<string>([...commonKeyIndexes.map(index => keys[index]), ProductKey.Variants]);
    // Positions of the keys that each variant keeps, for variants with the same keys as the first one
    const variantKeyIndexes = keys.map((_, index) => index).filter(index => !keysToOmit.has(keys[index]));

    const variants = group
      .map((variant, variantIndex) => {
        const values = rows[variantIndex];
        // Copies the kept values by their position, or by key name if the variant has other keys
        const variantValues: any = values
          ? variantKeyIndexes.reduce((result: any, index) => {
              result[keys[index]] = values[index];
              return result;
            }, {})
          : shallowOmit(keysToOmit, variant);
        variantValues[ProductKey.VariantId] = variant[ProductKey.VariantId];
        return variantValues as ProductVariant<Type>;
      })
      .filter(variant => !isEmpty(variant));

    /*
     * ATTENTION:
     * This is an performance optimization to avoid unnecessary object creation.
     */
    (commonValues as any)[ProductKey.ProductId] = group[0][ProductKey.ProductId];
    (commonValues as any)[ProductKey.ProductType] = group[0][ProductKey.ProductType];
    (commonValues as any)[ProductKey.Variants] = variants;

    return commonValues as Product<Type>;
  });
};

export const getFirstVariantId = <Type extends ProductType>(product: Product<Type>): ProductId => {
  if (product.variants.length > 0) {
    return product.variants[0][ProductKey.VariantId];
  }

  return product[ProductKey.VariantId] ?? product[ProductKey.ProductId];
};

export const getProductVariants = <Type extends ProductType>(product: Product<Type>): ProductVariant<Type>[] => {
  if (product[ProductKey.Variants].length > 0) {
    return product[ProductKey.Variants];
  }

  return [
    {
      ...shallowOmit(Object.values(ProductKey), product),
      [ProductKey.VariantId]: getFirstVariantId(product)
    } as unknown as ProductVariant<Type>
  ];
};

export const getMinPrice = <Type extends ProductType>(product: Product<Type>): number | undefined => {
  const prices = getAllSpecValues(ProductSpecKey.Price, product);
  return prices.length === 0 ? undefined : Math.min(...prices);
};

export const getMinOriginalPrice = <Type extends ProductType>(product: Product<Type>): number | undefined => {
  const originalPrices = getAllSpecValues(ProductSpecKey.OriginalPrice, product);
  return originalPrices.length === 0 ? undefined : Math.min(...originalPrices);
};

export const getMaxDiscountPercentage = <Type extends ProductType>(product: Product<Type>): number | undefined => {
  const discountPercentages = getAllSpecValues(ProductSpecKey.DiscountPercentage, product);
  return discountPercentages.length === 0 ? undefined : Math.max(...discountPercentages);
};

export const filterSpecGroups = <Type extends ProductType>(
  specGroups: SpecGroup<Type>[],
  showPrices = true
): SpecGroup<Type>[] =>
  specGroups.reduce<SpecGroup<Type>[]>((acc, specGroup) => {
    const nextSpecGroup = {
      ...specGroup,
      specKeys: specGroup.specKeys.filter(specKey => specKey !== ProductSpecKey.Price || showPrices)
    };

    acc.push(nextSpecGroup);

    return acc;
  }, []);

export const filterVariantSpecKeys = <Type extends ProductType>(
  specKeys: (keyof SpecMap[Type])[],
  showPrices = true
): (keyof SpecMap[Type])[] => specKeys.filter(specKey => specKey !== ProductSpecKey.Price || showPrices);

export const calcRelevantVariantSpecKeys = <Type extends ProductType>(
  variants: ProductVariant<Type>[],
  specDefinitions: SpecDefinitions<Type>,
  useCase: SpecDefinitionUseCase,
  priorityList: (keyof SpecMap[Type])[] = []
): (keyof SpecMap[Type])[] => {
  const variantSpecKeys = uniq(flatten(variants.map(variant => Object.keys(variant) as (keyof SpecMap[Type])[])))
    // Check whether key is enabled in spec definition for given use case.
    .filter(key => specDefinitions[key]?.enable?.[useCase]);

  const prioritizedVariantSpecKeys = priorityList.filter(key => variantSpecKeys.includes(key));
  const remainingVariantSpecKeys = difference(variantSpecKeys, priorityList);

  return [...prioritizedVariantSpecKeys, ...remainingVariantSpecKeys];
};

export const calcVariantSpecKeysForMinimalTableWithUniqueRows = <Type extends ProductType>(
  variants: ProductVariant<Type>[],
  specDefinitions: SpecDefinitions<Type>,
  useCase: SpecDefinitionUseCase,
  priorityList: (keyof SpecMap[Type])[] = []
): (keyof SpecMap[Type])[] => {
  const relevantVariantSpecKeys = calcRelevantVariantSpecKeys(variants, specDefinitions, useCase, priorityList);

  // Setup a temporary array where each index represents a table-row
  const tempVariantsTable: string[] = Array(variants.length).fill("");

  // Concat the values for each variant spec to a single, comparable "row-value" and add it to the temporary variants table
  const updateTempVariantsTable = (key: keyof SpecMap[Type]): void => {
    variants.forEach((variant, index) => (tempVariantsTable[index] += variant[key]));
  };

  // If the 'uniqued' array of row values has the same length as the raw values array, we know that each "row-value" is unique
  const isEachTableRowUnique = (): boolean => {
    const rowValues = Object.values(tempVariantsTable);
    return uniq(rowValues).length === rowValues.length;
  };

  const variantSpecKeys: (keyof SpecMap[Type])[] = [];

  for (const specKey of relevantVariantSpecKeys) {
    if (!isEachTableRowUnique()) {
      updateTempVariantsTable(specKey);
      variantSpecKeys.push(specKey);
    } else {
      // Avoid further unnecessary computation
      break;
    }
  }

  return variantSpecKeys;
};

export const filterEmptyAndEnsureMinimumVariantSpecKeys = <Type extends ProductType>(
  product: Product<Type>,
  specKeys: (keyof SpecMap[Type])[],
  specDefinitions: SpecDefinitions<Type>,
  fallbackSpecKeys: (keyof SpecMap[Type])[] = [],
  minimumSpecKeys = 2
): (keyof SpecMap[Type])[] => {
  const filteredSpecKeys = specKeys.filter(specKey => {
    const values = filterVisibleSpecValues(getAllSpecValues(specKey, product), specDefinitions[specKey]);
    return values.length > 0;
  });

  const filteredFallbackKeys = fallbackSpecKeys
    .filter(specKey => !filteredSpecKeys.includes(specKey))
    .filter(specKey => {
      const values = filterVisibleSpecValues(getAllSpecValues(specKey, product), specDefinitions[specKey]);
      return values.length > 0;
    });

  return [
    ...filteredSpecKeys,
    ...filteredFallbackKeys.slice(0, Math.max(0, minimumSpecKeys - filteredSpecKeys.length))
  ];
};

export const getFilterValuesForVariantsBySpecKey = <Type extends ProductType, K extends keyof SpecMap[Type]>(
  variants: StandaloneProductVariant<Type>[],
  specKey: K
): VariantFilterValues<Type, K>[] =>
  variants
    .reduce<VariantFilterValues<Type, K>[]>((acc, variant) => {
      const value = variant[specKey];

      if (acc.find(({ value: v }) => v === value)) {
        return acc;
      } else {
        return [...acc, { value, variant }];
      }
    }, [])
    .filter(({ value }) => !isNil(value));

export const getCategoryKeyFromProduct = <Type extends ProductType>(product: Product<Type>) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- CategoryKey should always be defined
  return product[ProductSpecKey.CategoryKey]! as CategoryMap[Type];
};
